import { Config, ApiUsageData, FinancialProductTypeEnum } from "./types";
import { Product } from "./product";

export interface ILeaseLinkWidget {
  isProductPage: boolean;
  isCheckout: boolean;
  showRate: boolean;
  products: Product[];
  /** Component-Placement reported to LeaseLink: card | summary | payments */
  componentPlacement: string;
}

export class CalculatorOnsite {
  scriptElementSrc =
    "https://elements.leaselink.pl/leaselinkelements/leaselinkelements.esm.js";
  calculatorElement!: HTMLElement;
  buttonElement!: HTMLElement;
  parentElement: HTMLElement | null = null;
  buildPromise: Promise<void> | null = null;

  apiKey: string | undefined;
  isSandbox: boolean = false;
  disableApplicationProcess: boolean = false;
  disableCartBuilder: boolean = false;
  enableCalculatorOnCheckout: boolean = false;
  renderToSelector: string | undefined;
  styleThemeId: string = "";
  calculatorStyle: string = "";
  buttonLabel: string = "Oblicz ratę w Leasingu";
  buttonStyle: string = "";
  buttonSize: string = "";
  buttonRadius: string = "";
  buttonShowRate: boolean = false;

  constructor(config: Config, public widget: ILeaseLinkWidget) {
    this.apiKey = config.apiKey;
    this.isSandbox = !!config.isSandbox;
    this.disableApplicationProcess = !!config.disableApplicationProcess || !!config.isCheckout;
    this.disableCartBuilder = !!config.disableCartBuilder;
    this.enableCalculatorOnCheckout = !!config.enableCalculatorOnCheckout;

    this.renderToSelector = config.renderToSelector;
    this.styleThemeId = config.styleThemeId || "";
    this.calculatorStyle = config.calculatorStyle || "";
    this.buttonLabel = config.buttonLabel || this.buttonLabel;
    this.buttonStyle = config.buttonStyle || "";
    this.buttonSize = config.buttonSize || "";
    this.buttonRadius = config.buttonRadius || "";
    this.buttonShowRate = !!config.buttonShowRate;

    this.buildPromise = this.init();

    const created = document.createElement("widget-leaselink-button") as HTMLElement & {
      dataset: DOMStringMap;
      updateButtonText?: (text: string) => void;
    };

    if (this.buttonStyle) {
      // Extract style number from style string (e.g., "style_1" -> "1")
      const styleMatch = this.buttonStyle.match(/\d+/);
      const styleNumber = styleMatch ? styleMatch[0] : this.buttonStyle;
      created.dataset.styleN = styleNumber;
    }
    if (this.buttonSize) {
      created.dataset.sizeN = this.buttonSize;
    }
    if (this.buttonRadius) {
      created.dataset.radius = this.buttonRadius;
    }
    if (this.buttonLabel) {
      created.dataset.label = this.buttonLabel;
    }

    this.buttonElement = created as HTMLElement;
  }

  async init(): Promise<void> {
    await this.loadElementsScript();

    this.calculatorElement = (window as any)
      .leaselinkElementsBuilder("div")
      .api({
        apiKey: this.apiKey,
      })
      .styles({
        externalId: this.calculatorStyle,
      })
      .calculator({
        disableProcess: this.disableApplicationProcess,
      })
      .build();

    // Środowisko testowe on-site. Builder .api() przekazuje tylko apiKey, więc
    // atrybut test-environment na <leaselink-api> ustawiamy ręcznie.
    if (this.isSandbox) {
      this.calculatorElement
        .getElementsByTagName("leaselink-api")[0]
        ?.setAttribute("test-environment", "true");
    }

    this.calculatorElement
      .getElementsByTagName("leaselink-calculator")[0]
      .setAttribute("disable-process", this.disableApplicationProcess.toString());

    if(this.calculatorStyle == 'STYL-WORDPRESS-01') {
      this.calculatorElement
        .getElementsByTagName("leaselink-calculator")[0]
        .setAttribute("white-logo", "true");
    }

    if (this.buttonElement) {
      this.buttonElement.addEventListener("click", () =>
        this.onClickedButton()
      );
    }
  }

  clearCart() {
    (window as any).leaselinkApi?.clearCart?.();
  }

  show() {
    (window as any).leaselinkApi?.showCalculator?.();
  }

  onClickedButton() {
    if (this.disableCartBuilder || this.widget.isCheckout) {
      this.clearCart();
    }

    this.widget.products.forEach((p) =>
      this.addItemToCart(p, {
        componentName: "onsite",
        componentPlacement: this.widget.componentPlacement,
        externalApp: "WordPress",
      })
    );

    this.show();
  }

  async addItemToCart(item: Product, apiUsageData: ApiUsageData) {
    (window as any).leaselinkApi?.addItemToCart?.(item, {
      disableDefault: true,
      apiUsageData,
    });
  }

  public updateButtonText(offer: {
    FinancialProductType: FinancialProductTypeEnum;
    NetMonthlyInstallment?: string;
    GrossMonthlyInstallment?: string;
    DeferralCostPercent?: string;
    DeferralPeriodDays?: string;
  }) {
    const {
      FinancialProductType,
      NetMonthlyInstallment,
      GrossMonthlyInstallment,
      DeferralPeriodDays,
    } = offer;
    let buttonText = "";

    switch (FinancialProductType) {
      case FinancialProductTypeEnum.OperationalLeasing:
        buttonText = this.widget.showRate
          ? `W Leasingu od ${NetMonthlyInstallment}`
          : "Weź Leasing teraz";
        break;
      case FinancialProductTypeEnum.Loan:
        buttonText = this.widget.showRate
          ? `Raty dla Firm od ${GrossMonthlyInstallment}`
          : "sprawdź Raty dla Firm";
        break;
      case FinancialProductTypeEnum.DeferredPayment:
        if (this.widget.showRate && DeferralPeriodDays) {
          buttonText = `Masz firmę? Zapłać za ${DeferralPeriodDays} dni`;
        } else {
          buttonText = "Masz firmę? Zapłać później";
        }
        break;
      case FinancialProductTypeEnum.Subscription:
        buttonText = this.widget.showRate
          ? `Abonament dla Firm od ${NetMonthlyInstallment}`
          : "Abonament dla Firm";
        break;
      default:
        buttonText = "Opcja finansowania";
    }


    if (this.buttonElement && (this.buttonElement as any).updateButtonText) {
      (this.buttonElement as any).updateButtonText(buttonText);
    }
  }

  async ensureBuilt(): Promise<HTMLElement> {
    if (!this.buildPromise) this.buildPromise = this.init();
    await this.buildPromise;
    return this.calculatorElement;
  }

  async loadElementsScript() {
    return new Promise((resolve, reject) => {
      // avoid loading duplicate script
      const existing = document.querySelector(
        `script[src="${this.scriptElementSrc}"]`
      );
      if (existing) return resolve(existing);

      const script = document.createElement("script");
      script.type = "module";
      script.src = this.scriptElementSrc;
      script.async = true;
      script.addEventListener("load", (event) => resolve(event));
      script.addEventListener("error", (ev) =>
        reject(new Error("Failed to load LeaseLink elements script"))
      );
      document.getElementsByTagName("head")[0].appendChild(script);
    });
  }
}
